import { z } from 'zod';
import type { ParseResult, SchemaIssue } from '../voyage/schemas.js';

/**
 * Mürettebat bordrosu (M16; ADR 0061): kaptanın gemide tuttuğu aylık ücret hesabı. Kişi başı
 * aylık satır: temel ücret + fazla mesai + diğer kazanç − avans − slopchest − allotment − diğer
 * kesinti = bankaya yatacak net [MLC 2006 Std A2.2 ¶1–¶4; B2.2.2]. Tutarlar kuruş (minor unit)
 * tamsayıdır; para birimi kişi başıdır, kur çevirisi yoktur (ADR 0061 §2). Saf modül.
 */

/** Tek tutarın üst sınırı: 10.000.000,00 (kuruş); PostgreSQL `integer` içinde kalır. */
export const MAX_PAYROLL_AMOUNT = 1_000_000_000;
export const DEFAULT_PAYROLL_CURRENCY = 'USD';
/** Seçicide önce gelen para birimleri; başka ISO 4217 kodu da girilebilir. */
export const PAYROLL_COMMON_CURRENCIES = [
  'USD',
  'EUR',
  'GBP',
  'TRY',
  'NOK',
  'SGD',
  'PHP',
  'INR',
  'UAH',
  'IDR',
] as const;

/** Bordroda kendi kalemi olan girişler: Cash Advance ve Slopchest (ADR 0061 §2, ADR 0066 §6). */
export const PAYROLL_ENTRY_KINDS = ['advance', 'slopchest'] as const;
/**
 * Kesinti türleri (ADR 0080 §2): gemi bordrolarında en sık görülen kesintiler, en çok kullanılan
 * önce, "Other" en sonda. Şirket kendi türünü ekler (`company` + `payroll_deduction_types`).
 * MLC'nin armatöre yüklediği masraflar (işe alma ve vize [MLC Std A1.4 ¶5(b)], geri dönüş
 * [Std A2.5.1 ¶3], gemide tıbbi bakım [Std A4.1 ¶1(d)]) ve izinsiz para cezası [B2.2.2 ¶4(j)] hazır
 * tür değildir. Kesinti yalnız ulusal mevzuat ya da TİS izin veriyorsa yapılır [B2.2.2 ¶4(h)].
 */
export const PAYROLL_DEDUCTION_TYPES = [
  'internet',
  'phone',
  'union_dues',
  'personal_expenses',
  'lost_equipment',
  'other',
] as const;
export type PayrollBuiltinDeductionType = (typeof PAYROLL_DEDUCTION_TYPES)[number];
/** Kesinti satırının türü: hazır tür ya da şirketin türü (`typeId`). */
export type PayrollDeductionKind = PayrollBuiltinDeductionType | 'company';
/** Girişin türü: avans, slopchest ya da kesinti (ADR 0080 §1). */
export type PayrollEntryKind = (typeof PAYROLL_ENTRY_KINDS)[number] | PayrollDeductionKind;
/** Girişin bütün türleri, seçici ve şema sırasıyla. */
export const PAYROLL_ENTRY_ALL_KINDS = [
  ...PAYROLL_ENTRY_KINDS,
  ...PAYROLL_DEDUCTION_TYPES,
  'company',
] as const satisfies readonly PayrollEntryKind[];
/** Açıklaması zorunlu kesinti türleri: ne için kesildiği yazılır (kalem kalem [MLC A2.2 ¶2]). */
export const PAYROLL_DEDUCTION_DESCRIBED: readonly PayrollEntryKind[] = ['lost_equipment', 'other'];
export const PAYROLL_DEDUCTION_TYPE_NAME_MAX = 100;

/** Girişin türü "Other Deductions" kalemine mi gider (avans ve slopchest değil; ADR 0080 §3). */
export function isPayrollDeductionKind(kind: unknown): kind is PayrollDeductionKind {
  return kind === 'company' || (PAYROLL_DEDUCTION_TYPES as readonly unknown[]).includes(kind);
}
export const PAYROLL_ALLOTMENT_BASES = ['fixed', 'percent'] as const;
export type PayrollAllotmentBasis = (typeof PAYROLL_ALLOTMENT_BASES)[number];
export const PAYROLL_PAYMENT_TYPES = ['salary', 'allotment', 'final_settlement'] as const;
export type PayrollPaymentType = (typeof PAYROLL_PAYMENT_TYPES)[number];
export const PAYROLL_PAYMENT_METHODS = ['bank_transfer', 'cash_on_board'] as const;
export type PayrollPaymentMethod = (typeof PAYROLL_PAYMENT_METHODS)[number];
export const PAYROLL_PAYMENT_STATUSES = ['recorded', 'reconciled'] as const;
export type PayrollPaymentStatus = (typeof PAYROLL_PAYMENT_STATUSES)[number];
export const PAYROLL_PERIOD_STATUSES = ['open', 'closed'] as const;
export type PayrollPeriodStatus = (typeof PAYROLL_PERIOD_STATUSES)[number];
/**
 * Extra (ek kazanç) kalemlerinin hazır türleri (ADR 0068 §2): ITF/IBF toplu sözleşme kalemleri ve
 * bordro yazılımı emsalleri; en çok kullanılan önce, "Other" en sonda. Şirket kendi türünü ekler
 * (`company` + `payroll_extra_types`).
 */
export const PAYROLL_EXTRA_TYPES = [
  'fixed_overtime',
  'extra_overtime',
  'leave_pay',
  'subsistence',
  'bonus',
  'hazardous_cargo',
  'high_risk_area',
  'hold_cleaning',
  'lashing',
  'additional_duty',
  'night_work',
  'port_allowance',
  'travel_allowance',
  'reimbursement',
  'other',
] as const;
export type PayrollBuiltinExtraType = (typeof PAYROLL_EXTRA_TYPES)[number];
/** Satırdaki tür: hazır tür ya da şirketin türü (`typeId`). */
export type PayrollExtraType = PayrollBuiltinExtraType | 'company';
export const PAYROLL_EXTRA_TYPE_NAME_MAX = 100;

/** Ödenecek tutara sayılan ödeme türleri (ADR 0061 §4); final settlement ayrı hesaptır. */
export const PAYABLE_PAYMENT_TYPES: readonly PayrollPaymentType[] = ['salary', 'allotment'];

/** Gerekçe sınırları: dönemi yeniden açma ve kayıttan vazgeçme (ADR 0061 §1, §3). */
export const PAYROLL_REOPEN_REASON = { min: 5, max: 2000 } as const;
export const PAYROLL_VOID_REASON = { min: 5, max: 500 } as const;

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;
const DAY = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

export function isPayrollMonth(value: unknown): value is string {
  return typeof value === 'string' && MONTH.test(value);
}

export function daysInPayrollMonth(month: string): number {
  const [year, mon] = month.split('-').map(Number);
  return new Date(Date.UTC(year ?? 1970, mon ?? 1, 0)).getUTCDate();
}

/** Günün (`YYYY-MM-DD`) bordro ayı. */
export function payrollMonthOf(day: string): string {
  return day.slice(0, 7);
}

/** Dönem bu ay ya da geçmiş bir ay olabilir; gelecek ay açılmaz (`FUTURE_PERIOD`). */
export function payrollPeriodOpenable(month: string, today: string): boolean {
  return isPayrollMonth(month) && month <= payrollMonthOf(today);
}

/** Bir ay önceki ay. */
export function previousPayrollMonth(month: string): string {
  const [year, mon] = month.split('-').map(Number);
  const date = new Date(Date.UTC(year ?? 1970, (mon ?? 1) - 2, 1));
  return date.toISOString().slice(0, 7);
}

/** Pozitif `a / b`'nin yarım yukarı yuvarlanmış tamsayısı (tamsayı aritmetiği). */
function divideRound(a: number, b: number): number {
  return Math.floor((2 * a + b) / (2 * b));
}

/**
 * Temel ücret: aylık ücret × gemide gün / ayın gün sayısı; tam ay aylık ücrettir (ADR 0061 §2).
 * Kuruş, yarım yukarı.
 */
export function earnedBasic(monthlyWage: number, daysOnBoard: number, month: string): number {
  const days = daysInPayrollMonth(month);
  if (daysOnBoard >= days) return monthlyWage;
  if (daysOnBoard <= 0) return 0;
  return divideRound(monthlyWage * daysOnBoard, days);
}

/** Fazla mesai tutarı: saat (bir ondalık) × saatlik oran (kuruş), yarım yukarı [MLC B2.2.2]. */
export function overtimeAmountOf(hours: number, rate: number): number {
  return divideRound(Math.round(hours * 10) * rate, 10);
}

export interface PayrollLineData {
  id: string;
  userId: string;
  name: string;
  rank: string | null;
  currency: string;
  daysOnBoard: number;
  monthlyWage: number;
  overtimeHours: number | null;
  overtimeRate: number | null;
  overtimeAmount: number;
  otherEarnings: number;
  otherEarningsNote: string | null;
  otherDeductions: number;
  otherDeductionsNote: string | null;
  remarks: string | null;
}

export interface PayrollEntryData {
  id: string;
  userId: string;
  kind: PayrollEntryKind;
  entryDay: string;
  amount: number;
  currency: string;
  description: string | null;
  /** Şirket kesinti türünün kimliği ve adları (yalnız `company`; ADR 0080 §2). */
  typeId?: string | null;
  typeName?: string | null;
  typeNameTr?: string | null;
}

export interface PayrollAllotmentData {
  id: string;
  userId: string;
  beneficiaryName: string;
  relationship: string | null;
  bankDetails: string | null;
  basis: PayrollAllotmentBasis;
  amount: number | null;
  percentBp: number | null;
  currency: string;
  startMonth: string;
  endMonth: string | null;
}

/** Extra kalemi (ADR 0068 §1): kişi-dönem başına çok satır; tutar kuruş, para birimi kişinin. */
export interface PayrollExtraData {
  id: string;
  userId: string;
  type: PayrollExtraType;
  /** Şirket türünün kimliği ve adı (yalnız `company`). */
  typeId: string | null;
  typeName: string | null;
  description: string | null;
  /** Miktar (saat, ambar, gün; bir ondalık) ve birim ücret (kuruş); ikisi de isteğe bağlı. */
  quantity: number | null;
  rate: number | null;
  amount: number;
  currency: string;
}

/** Dökümdeki extra kalemi: `legacy` satırın eski tek alanından türetilmiş (ADR 0068 §4). */
export type PayrollExtraItem = Omit<PayrollExtraData, 'userId'> & { legacy?: boolean };

/** Allotment o ayda geçerli mi (başlangıç ve bitiş ayları dahil). */
export function allotmentActive(
  allotment: Pick<PayrollAllotmentData, 'startMonth' | 'endMonth'>,
  month: string,
): boolean {
  return (
    allotment.startMonth <= month && (allotment.endMonth === null || allotment.endMonth >= month)
  );
}

/** Ayın allotment tutarı: sabit tutar ya da temel ücretin yüzdesi (baz puan; ADR 0061 §2). */
export function allotmentAmount(
  allotment: Pick<PayrollAllotmentData, 'basis' | 'amount' | 'percentBp'>,
  basic: number,
): number {
  if (allotment.basis === 'fixed') return allotment.amount ?? 0;
  return divideRound(basic * (allotment.percentBp ?? 0), 10_000);
}

export interface PayrollEntryItem {
  id: string;
  day: string;
  amount: number;
  currency: string;
  description: string | null;
}

/**
 * Kesinti dökümünün kalemi (ADR 0080 §3): türlü kesinti girişi; `legacy` satırın eski tek "diğer
 * kesinti" alanından türetilmiş (günü yok).
 */
export interface PayrollDeductionItem {
  id: string;
  day: string | null;
  type: PayrollDeductionKind;
  typeId: string | null;
  typeName: string | null;
  typeNameTr: string | null;
  description: string | null;
  amount: number;
  currency: string;
  legacy?: boolean;
}

export interface PayrollAllotmentItem {
  id: string;
  beneficiaryName: string;
  relationship: string | null;
  bankDetails: string | null;
  basis: PayrollAllotmentBasis;
  percentBp: number | null;
  currency: string;
  amount: number;
}

export interface PayrollLineResult extends PayrollLineData {
  basic: number;
  /** Extra kalemleri (`payroll_extras`); eski tek alanlar burada değil, `payrollExtraItems`. */
  extras: PayrollExtraItem[];
  extraTotal: number;
  earnings: number;
  advances: number;
  advanceEntries: PayrollEntryItem[];
  slopchest: number;
  slopchestEntries: PayrollEntryItem[];
  /** Avans ve slopchest dışındaki kesinti girişleri, gün sırasıyla (ADR 0080 §3). */
  deductionEntries: PayrollDeductionItem[];
  deductionTotal: number;
  allotments: PayrollAllotmentItem[];
  allotmentTotal: number;
  deductions: number;
  net: number;
}

export interface PayrollCurrencyTotal {
  currency: string;
  people: number;
  earnings: number;
  deductions: number;
  allotments: number;
  net: number;
  /** Bankaya net + allotment: ödenecek toplam (ADR 0061 §4). */
  payable: number;
}

export type PayrollIssueCode =
  | 'NO_LINES'
  | 'NEGATIVE_NET'
  | 'CURRENCY_MISMATCH'
  | 'ENTRY_WITHOUT_LINE';

export interface PayrollIssue {
  code: PayrollIssueCode;
  userId: string | null;
}

export interface PayrollComputation {
  month: string;
  lines: PayrollLineResult[];
  totals: PayrollCurrencyTotal[];
  /** Kapanışı engelleyen sorunlar (ADR 0061 §3); boşsa dönem kapanabilir. */
  issues: PayrollIssue[];
}

const entryItem = (e: PayrollEntryData): PayrollEntryItem => ({
  id: e.id,
  day: e.entryDay,
  amount: e.amount,
  currency: e.currency,
  description: e.description,
});

const deductionItem = (e: PayrollEntryData): PayrollDeductionItem => ({
  id: e.id,
  day: e.entryDay,
  type: e.kind as PayrollDeductionKind,
  typeId: e.typeId ?? null,
  typeName: e.typeName ?? null,
  typeNameTr: e.typeNameTr ?? null,
  description: e.description,
  amount: e.amount,
  currency: e.currency,
});

const byDay = (a: PayrollEntryData, b: PayrollEntryData) =>
  a.entryDay.localeCompare(b.entryDay) || a.id.localeCompare(b.id);

/**
 * Dönemin hesabı (ADR 0061 §2): satır sırası verildiği gibidir; toplamlar para birimine göre
 * (alfabetik). Para birimi uyuşmayan giriş ya da allotment hesaba girer ama kapanışı engeller.
 */
export function computePayrollPeriod(input: {
  month: string;
  lines: readonly PayrollLineData[];
  entries: readonly PayrollEntryData[];
  allotments: readonly PayrollAllotmentData[];
  /** Extra kalemleri (ADR 0068); verilmezse yok. */
  extras?: readonly PayrollExtraData[];
}): PayrollComputation {
  const { month } = input;
  const extras = input.extras ?? [];
  const issues: PayrollIssue[] = [];
  const add = (code: PayrollIssueCode, userId: string | null) => {
    if (!issues.some((i) => i.code === code && i.userId === userId)) issues.push({ code, userId });
  };
  if (input.lines.length === 0) add('NO_LINES', null);
  const withLine = new Set(input.lines.map((l) => l.userId));
  for (const e of input.entries) if (!withLine.has(e.userId)) add('ENTRY_WITHOUT_LINE', e.userId);
  for (const x of extras) if (!withLine.has(x.userId)) add('ENTRY_WITHOUT_LINE', x.userId);

  const lines = input.lines.map((l): PayrollLineResult => {
    const basic = earnedBasic(l.monthlyWage, l.daysOnBoard, month);
    const own = input.entries.filter((e) => e.userId === l.userId).sort(byDay);
    const advanceEntries = own.filter((e) => e.kind === 'advance');
    const slopchestEntries = own.filter((e) => e.kind === 'slopchest');
    const deductionEntries = own.filter((e) => isPayrollDeductionKind(e.kind));
    const sum = (items: readonly { amount: number }[]) =>
      items.reduce((total, item) => total + item.amount, 0);
    const allotments = input.allotments
      .filter((a) => a.userId === l.userId && allotmentActive(a, month))
      .sort((a, b) => a.startMonth.localeCompare(b.startMonth) || a.id.localeCompare(b.id))
      .map(
        (a): PayrollAllotmentItem => ({
          id: a.id,
          beneficiaryName: a.beneficiaryName,
          relationship: a.relationship,
          bankDetails: a.bankDetails,
          basis: a.basis,
          percentBp: a.percentBp,
          currency: a.currency,
          amount: allotmentAmount(a, basic),
        }),
      );
    const ownExtras = extras
      .filter((x) => x.userId === l.userId)
      .map(({ userId: _userId, ...item }): PayrollExtraItem => item);
    if (
      own.some((e) => e.currency !== l.currency) ||
      allotments.some((a) => a.currency !== l.currency) ||
      ownExtras.some((x) => x.currency !== l.currency)
    ) {
      add('CURRENCY_MISMATCH', l.userId);
    }
    const extraTotal = sum(ownExtras);
    const earnings = basic + l.overtimeAmount + l.otherEarnings + extraTotal;
    const advances = sum(advanceEntries);
    const slopchest = sum(slopchestEntries);
    const deductionTotal = sum(deductionEntries);
    const allotmentTotal = sum(allotments);
    // Kesinti sırası (ADR 0080 §3): Home Allotment, Cash Advance, Slopchest, Other Deductions
    const deductions = advances + slopchest + allotmentTotal + l.otherDeductions + deductionTotal;
    const net = earnings - deductions;
    if (net < 0) add('NEGATIVE_NET', l.userId);
    return {
      ...l,
      basic,
      extras: ownExtras,
      extraTotal,
      earnings,
      advances,
      advanceEntries: advanceEntries.map(entryItem),
      slopchest,
      slopchestEntries: slopchestEntries.map(entryItem),
      deductionEntries: deductionEntries.map(deductionItem),
      deductionTotal,
      allotments,
      allotmentTotal,
      deductions,
      net,
    };
  });

  const totals = new Map<string, PayrollCurrencyTotal>();
  for (const l of lines) {
    const t = totals.get(l.currency) ?? {
      currency: l.currency,
      people: 0,
      earnings: 0,
      deductions: 0,
      allotments: 0,
      net: 0,
      payable: 0,
    };
    t.people += 1;
    t.earnings += l.earnings;
    t.deductions += l.deductions;
    t.allotments += l.allotmentTotal;
    t.net += l.net;
    t.payable += l.net + l.allotmentTotal;
    totals.set(l.currency, t);
  }
  return {
    month,
    lines,
    totals: [...totals.values()].sort((a, b) => a.currency.localeCompare(b.currency)),
    issues,
  };
}

type ExtraLine = Pick<
  PayrollLineData,
  | 'id'
  | 'currency'
  | 'overtimeHours'
  | 'overtimeRate'
  | 'overtimeAmount'
  | 'otherEarnings'
  | 'otherEarningsNote'
> & { extras?: readonly PayrollExtraItem[] | undefined };

/**
 * Kişinin Extra dökümü (ADR 0068 §4): satırın eski tek alanları (fazla mesai: saatliyse
 * `extra_overtime`, değilse `fixed_overtime`; diğer kazanç: `other`) ve extra kalemleri. Eski
 * kapanış özetinde `extras` yoktur; döküm yine çıkar.
 */
export function payrollExtraItems(line: ExtraLine): PayrollExtraItem[] {
  const legacy: PayrollExtraItem[] = [];
  const base = { typeId: null, typeName: null, currency: line.currency, legacy: true };
  if (line.overtimeAmount > 0) {
    const hourly = line.overtimeHours !== null && line.overtimeRate !== null;
    legacy.push({
      ...base,
      id: `${line.id}:overtime`,
      type: hourly ? 'extra_overtime' : 'fixed_overtime',
      description: null,
      quantity: hourly ? line.overtimeHours : null,
      rate: hourly ? line.overtimeRate : null,
      amount: line.overtimeAmount,
    });
  }
  if (line.otherEarnings > 0) {
    legacy.push({
      ...base,
      id: `${line.id}:other`,
      type: 'other',
      description: line.otherEarningsNote,
      quantity: null,
      rate: null,
      amount: line.otherEarnings,
    });
  }
  return [...legacy, ...(line.extras ?? []).map((x) => ({ ...x, legacy: false }))];
}

/** Kişinin Extra toplamı: eski tek alanlar + extra kalemleri (kuruş). */
export function payrollExtraTotal(line: ExtraLine): number {
  return payrollExtraItems(line).reduce((total, x) => total + x.amount, 0);
}

type DeductionLine = Pick<PayrollLineData, 'id' | 'currency' | 'otherDeductions'> & {
  otherDeductionsNote?: string | null | undefined;
  deductionEntries?: readonly PayrollDeductionItem[] | undefined;
};

/**
 * Kişinin "Other Deductions" dökümü (ADR 0080 §3, §4): satırın eski tek "diğer kesinti" alanı
 * (`other`, açıklamasıyla) ve türlü kesinti girişleri. Eski kapanış özetinde `deductionEntries`
 * yoktur; döküm yine çıkar.
 */
export function payrollDeductionItems(line: DeductionLine): PayrollDeductionItem[] {
  const legacy: PayrollDeductionItem[] =
    line.otherDeductions > 0
      ? [
          {
            id: `${line.id}:other-deduction`,
            day: null,
            type: 'other',
            typeId: null,
            typeName: null,
            typeNameTr: null,
            description: line.otherDeductionsNote ?? null,
            amount: line.otherDeductions,
            currency: line.currency,
            legacy: true,
          },
        ]
      : [];
  return [...legacy, ...(line.deductionEntries ?? []).map((x) => ({ ...x, legacy: false }))];
}

/** Kişinin "Other Deductions" toplamı: eski tek alan + türlü kesintiler (kuruş). */
export function payrollOtherDeductionTotal(line: DeductionLine): number {
  return payrollDeductionItems(line).reduce((total, x) => total + x.amount, 0);
}

export interface PayrollProgress {
  currency: string;
  payable: number;
  recorded: number;
  outstanding: number;
  /** 0–1. */
  progress: number;
}

/**
 * Özet kartları (ADR 0061 §4): para birimine göre ödenecek, kaydedilen (Salary + Allotment),
 * kalan (eksiye düşmez) ve ilerleme.
 */
export function paymentProgress(
  totals: readonly Pick<PayrollCurrencyTotal, 'currency' | 'payable'>[],
  payments: readonly { currency: string; amount: number; paymentType: PayrollPaymentType }[],
): PayrollProgress[] {
  const rows = new Map<string, PayrollProgress>();
  const row = (currency: string) => {
    const found = rows.get(currency);
    if (found) return found;
    const created = { currency, payable: 0, recorded: 0, outstanding: 0, progress: 0 };
    rows.set(currency, created);
    return created;
  };
  for (const t of totals) row(t.currency).payable += t.payable;
  for (const p of payments) {
    if (PAYABLE_PAYMENT_TYPES.includes(p.paymentType)) row(p.currency).recorded += p.amount;
  }
  return [...rows.values()]
    .map((r) => ({
      ...r,
      outstanding: Math.max(0, r.payable - r.recorded),
      progress: r.payable > 0 ? Math.min(1, r.recorded / r.payable) : r.recorded > 0 ? 1 : 0,
    }))
    .sort((a, b) => a.currency.localeCompare(b.currency));
}

/* ---------- Şemalar (gemi, bulut ve arayüz ortak) ---------- */

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const amount = (min = 0) =>
  z
    .number(typed('INVALID_AMOUNT'))
    .int('INVALID_AMOUNT')
    .min(min, 'INVALID_AMOUNT')
    .max(MAX_PAYROLL_AMOUNT, 'TOO_LARGE');
const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform((value) => value.normalize('NFC').trim())
    .pipe(z.string().max(max, 'TOO_LONG'))
    .nullish()
    .transform((value) => (value ? value : null));
const requiredText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform((value) => value.normalize('NFC').trim())
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));
const currency = () =>
  z
    .string(typed('INVALID_CURRENCY'))
    .transform((value) => value.trim().toUpperCase())
    .pipe(z.string().regex(/^[A-Z]{3}$/, 'INVALID_CURRENCY'));
const month = () => z.string(typed('INVALID_MONTH')).regex(MONTH, 'INVALID_MONTH');
const day = () =>
  z
    .string(typed('INVALID_DATE'))
    .regex(DAY, 'INVALID_DATE')
    .refine((value) => {
      const date = new Date(`${value}T00:00:00Z`);
      return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
    }, 'INVALID_DATE');

function issuesOf(error: z.ZodError): SchemaIssue[] {
  return error.issues.map((issue) => ({
    path: issue.path.map(String).join('.'),
    code: issue.message,
  }));
}

function parser<T extends z.ZodType>(schema: T) {
  return (input: unknown): ParseResult<z.output<T>> => {
    const result = schema.safeParse(input);
    return result.success
      ? { ok: true, value: result.data }
      : { ok: false, issues: issuesOf(result.error) };
  };
}

const lineSchema = z
  .object({
    userId: z.uuid(typed('INVALID_USER')),
    currency: currency(),
    daysOnBoard: z
      .number(typed('INVALID_DAYS'))
      .int('INVALID_DAYS')
      .min(0, 'INVALID_DAYS')
      .max(31, 'INVALID_DAYS'),
    monthlyWage: amount(),
    overtimeHours: z
      .number(typed('INVALID_HOURS'))
      .min(0, 'INVALID_HOURS')
      .max(744, 'INVALID_HOURS')
      .refine(
        (v) => Number.isInteger(Math.round(v * 10)) && Math.abs(v * 10 - Math.round(v * 10)) < 1e-9,
        'INVALID_HOURS',
      )
      .nullish()
      .default(null),
    overtimeRate: amount().nullish().default(null),
    overtimeAmount: amount().default(0),
    otherEarnings: amount().default(0),
    otherEarningsNote: optionalText(200),
    otherDeductions: amount().default(0),
    otherDeductionsNote: optionalText(200),
    remarks: optionalText(500),
  })
  .superRefine((value, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    if (
      value.overtimeHours !== null &&
      value.overtimeRate !== null &&
      value.overtimeAmount !== overtimeAmountOf(value.overtimeHours, value.overtimeRate)
    ) {
      issue('overtimeAmount', 'OVERTIME_MISMATCH');
    }
    if (value.otherEarnings > 0 && value.otherEarningsNote === null)
      issue('otherEarningsNote', 'REQUIRED');
    if (value.otherDeductions > 0 && value.otherDeductionsNote === null) {
      issue('otherDeductionsNote', 'REQUIRED');
    }
  });
export type PayrollLineInput = z.output<typeof lineSchema>;
/** Satırın alanları; dönem, kişi ve kilit kuralları db'de (ADR 0061 §1–§3). */
export const parsePayrollLine = parser(lineSchema);

const entrySchema = z
  .object({
    userId: z.uuid(typed('INVALID_USER')),
    kind: z.enum(PAYROLL_ENTRY_ALL_KINDS, typed('INVALID_OPTION')),
    typeId: z.uuid(typed('INVALID_TYPE')).nullish().default(null),
    entryDay: day(),
    amount: amount(1),
    currency: currency(),
    description: optionalText(200),
  })
  .superRefine((value, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    if (value.kind === 'company' && value.typeId === null) issue('typeId', 'REQUIRED');
    if (value.kind !== 'company' && value.typeId !== null) issue('typeId', 'NOT_ALLOWED');
    if (PAYROLL_DEDUCTION_DESCRIBED.includes(value.kind) && value.description === null) {
      issue('description', 'REQUIRED');
    }
  });
export type PayrollEntryInput = z.output<typeof entrySchema>;
/** Avans, slopchest ve kesinti girişi (ADR 0061 §2; ADR 0080 §1). */
export const parsePayrollEntry = parser(entrySchema);

const extraSchema = z
  .object({
    userId: z.uuid(typed('INVALID_USER')),
    type: z.enum([...PAYROLL_EXTRA_TYPES, 'company'], typed('INVALID_OPTION')),
    typeId: z.uuid(typed('INVALID_TYPE')).nullish().default(null),
    description: optionalText(200),
    quantity: z
      .number(typed('INVALID_QUANTITY'))
      .min(0, 'INVALID_QUANTITY')
      .max(9999, 'INVALID_QUANTITY')
      .refine((v) => Math.abs(v * 10 - Math.round(v * 10)) < 1e-9, 'INVALID_QUANTITY')
      .nullish()
      .default(null),
    rate: amount().nullish().default(null),
    amount: amount(1),
    currency: currency(),
  })
  .superRefine((value, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    if (value.type === 'company' && value.typeId === null) issue('typeId', 'REQUIRED');
    if (value.type !== 'company' && value.typeId !== null) issue('typeId', 'NOT_ALLOWED');
    if (value.type === 'other' && value.description === null) issue('description', 'REQUIRED');
    // Miktar × birim ücret verilirse tutar odur (fazla mesai kaydı [MLC B2.2.2])
    if (
      value.quantity !== null &&
      value.rate !== null &&
      value.amount !== overtimeAmountOf(value.quantity, value.rate)
    ) {
      issue('amount', 'AMOUNT_MISMATCH');
    }
  });
export type PayrollExtraInput = z.output<typeof extraSchema>;
/** Extra kalemi (ADR 0068 §1): tür, açıklama, isteğe bağlı miktar × birim ücret, tutar. */
export const parsePayrollExtra = parser(extraSchema);

const extraTypeSchema = z.object({ name: requiredText(PAYROLL_EXTRA_TYPE_NAME_MAX) });
/** Şirketin extra türü (ADR 0068 §2): ad 1–100. */
export const parsePayrollExtraType = parser(extraTypeSchema);

const deductionTypeSchema = z.object({
  name: requiredText(PAYROLL_DEDUCTION_TYPE_NAME_MAX),
  nameTr: optionalText(PAYROLL_DEDUCTION_TYPE_NAME_MAX),
  active: z.boolean(typed('INVALID_OPTION')).default(true),
});
export type PayrollDeductionTypeInput = z.output<typeof deductionTypeSchema>;
/** Şirketin kesinti türü (ADR 0080 §2): İngilizce ad 1–100, Türkçe ad isteğe bağlı, etkin/pasif. */
export const parsePayrollDeductionType = parser(deductionTypeSchema);

const allotmentSchema = z
  .object({
    userId: z.uuid(typed('INVALID_USER')),
    beneficiaryName: requiredText(200),
    relationship: optionalText(100),
    bankDetails: optionalText(500),
    basis: z.enum(PAYROLL_ALLOTMENT_BASES, typed('INVALID_OPTION')),
    amount: amount(1).nullish().default(null),
    percentBp: z
      .number(typed('INVALID_PERCENT'))
      .int('INVALID_PERCENT')
      .min(1, 'INVALID_PERCENT')
      .max(10_000, 'INVALID_PERCENT')
      .nullish()
      .default(null),
    currency: currency(),
    startMonth: month(),
    endMonth: month().nullish().default(null),
    remarks: optionalText(500),
  })
  .superRefine((value, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    if (value.basis === 'fixed') {
      if (value.amount === null) issue('amount', 'REQUIRED');
      if (value.percentBp !== null) issue('percentBp', 'NOT_ALLOWED');
    } else {
      if (value.percentBp === null) issue('percentBp', 'REQUIRED');
      if (value.amount !== null) issue('amount', 'NOT_ALLOWED');
    }
    if (value.endMonth !== null && value.endMonth < value.startMonth)
      issue('endMonth', 'END_BEFORE_START');
  });
export type PayrollAllotmentInput = z.output<typeof allotmentSchema>;
/** Allotment tanımı: alıcı, sabit tutar ya da yüzde, ay aralığı (ADR 0061 §2) [MLC A2.2 ¶3–¶4]. */
export const parsePayrollAllotment = parser(allotmentSchema);

const paymentSchema = z
  .object({
    userId: z.uuid(typed('INVALID_USER')),
    paymentType: z.enum(PAYROLL_PAYMENT_TYPES, typed('INVALID_OPTION')),
    allotmentId: z.uuid(typed('INVALID_ALLOTMENT')).nullish().default(null),
    amount: amount(1),
    currency: currency(),
    paymentDay: day(),
    method: z.enum(PAYROLL_PAYMENT_METHODS, typed('INVALID_OPTION')),
    reference: optionalText(100),
    bankAccount: optionalText(200),
    remarks: optionalText(500),
  })
  .superRefine((value, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    if (value.method === 'bank_transfer' && value.reference === null) {
      issue('reference', 'REFERENCE_REQUIRED');
    }
    if (value.allotmentId !== null && value.paymentType !== 'allotment') {
      issue('allotmentId', 'ALLOTMENT_ONLY');
    }
  });
export type PayrollPaymentInput = z.output<typeof paymentSchema>;
/** Ödeme defteri kaydı (ADR 0061 §4). */
export const parsePayrollPayment = parser(paymentSchema);

/** Gerekçe metni sınırlar içinde mi (boşluklar kırpılarak). */
export function validReason(value: unknown, limits: { min: number; max: number }): value is string {
  if (typeof value !== 'string') return false;
  const text = value.trim();
  return text.length >= limits.min && text.length <= limits.max;
}
